import { discoveryApi } from '@/api/clients/discoveryApi'
import { playbookApi } from '@/api/clients/playbookApi'
import type * as discovery from '@/api/types/discovery'
import type * as playbook from '@/api/types/playbook'
import type { DiscoveryFacts, PlaybookFacts } from '@/api/types/readiness'

// ── Discovery facts (§3.2 signals, §7 sync) ─────────────────────────────────
// qtz-readiness keeps a copy of the organisation's Discovery (Module 1)
// inventories, sources and crypto assets and serves it under /discovery/*
// (discoveryApi). A sync first asks it to pull from Discovery, then reads the
// summary and the systems. The prioritised flag, the CBOM system ids, the
// certificate / key signal and the vendor components are computed server-side
// with the rules the signals used to apply here. A manual sync waits for the
// pull so the user sees fresh figures; the scheduled and on-open runs start it
// in the background and read what the backend already holds. Any failure on
// the summary call means "Discovery not connected" (§13): sync then skips the
// Discovery signals and the header shows the not-connected state.

export const EMPTY_DISCOVERY_FACTS: DiscoveryFacts = {
  connected: false,
  inventories: [],
  cbom_inventory_ids: [],
  source_count: 0,
  asset_count: 0,
  vulnerable_count: 0,
  cert_key_inventory: false,
  vendor_components: [],
  quantum_risk: null,
}

// 409 (a pull is already running) and 503 (Discovery not configured for the
// organisation) are expected here; the read that follows reports whatever the
// backend holds, and its summary carries the last sync's status and error.
async function pullFromDiscovery(orgId: string, trigger: discovery.SyncTrigger): Promise<void> {
  await discoveryApi.sync(orgId, { trigger, wait: trigger === 'manual' }).catch(() => undefined)
}

export async function fetchDiscoveryFacts(orgId: string, trigger: discovery.SyncTrigger = 'manual'): Promise<DiscoveryFacts> {
  if (!orgId) return EMPTY_DISCOVERY_FACTS

  await pullFromDiscovery(orgId, trigger)
  const [sumR, sysR] = await Promise.allSettled([discoveryApi.summary(orgId), discoveryApi.systems(orgId)])
  if (sumR.status !== 'fulfilled') return EMPTY_DISCOVERY_FACTS
  const summary = sumR.value
  const systems = sysR.status === 'fulfilled' ? sysR.value : []

  return {
    connected: summary.connected,
    inventories: systems.map((s) => ({
      id: s.id,
      name: s.name,
      description: s.description ?? '',
      business_unit: s.business_unit ?? '',
      criticality: s.criticality ?? '',
      tags: s.tags ?? [],
      prioritised: s.prioritised,
      cii: s.cii,
      asset_count: s.asset_count,
      environment: s.environment ?? '',
      has_cbom: s.has_cbom,
      last_scan_at: s.last_scan_at,
    })),
    cbom_inventory_ids: summary.cbom_system_ids ?? [],
    source_count: summary.source_count,
    asset_count: summary.asset_count,
    vulnerable_count: summary.quantum_risk?.vulnerable ?? 0,
    cert_key_inventory: summary.cert_key_inventory,
    // A vendor component is a source; it may serve several systems, the facts
    // keep the first as its inventory.
    vendor_components: (summary.vendor_components ?? []).map((v) => ({ id: v.id, name: v.name, type: v.type, inventory_id: v.system_ids?.[0] ?? null })),
    quantum_risk: summary.quantum_risk ?? null,
  }
}

// ── Playbook facts ──────────────────────────────────────────────────────────
// Two sources. qtz-readiness keeps a copy of Module 3 (qtz-playbook)'s Issuing
// CA register and the NIST remediation report over Module 1's crypto assets,
// served under /playbook/* (playbookApi): the Issuing CAs are EE-1's
// third-party certificate dependencies, the remediation rows are TE-1's
// quantum-safe replacement options. A sync first asks it to pull the register
// from Playbook (the manual sync waits, the others read what the backend
// holds), then reads the summary, the register and the report; any failure
// on the summary means "Playbook not connected" and the `playbook.*` signals
// that need it are not met. The plans stay local (Open Question 9): the
// actions this module creates from recommendations are the Playbook plan set,
// and mergePlaybookFacts puts both under one PlaybookFacts.

export const EMPTY_PLAYBOOK_FACTS: PlaybookFacts = {
  connected: true,
  service_connected: false,
  plans: [],
  issuing_cas: [],
  remediations: [],
  standard: null,
}

// 409 (a pull is already running) and 503 (no Playbook URL on the service) are
// expected here; the reads that follow report whatever the backend holds.
async function pullFromPlaybook(orgId: string, trigger: playbook.SyncTrigger): Promise<void> {
  await playbookApi.sync(orgId, { trigger, wait: trigger === 'manual' }).catch(() => undefined)
}

export async function fetchPlaybookFacts(orgId: string, trigger: playbook.SyncTrigger = 'manual'): Promise<PlaybookFacts> {
  if (!orgId) return EMPTY_PLAYBOOK_FACTS

  await pullFromPlaybook(orgId, trigger)
  const [sumR, casR, remR] = await Promise.allSettled([playbookApi.summary(orgId), playbookApi.issuingCas(orgId), playbookApi.remediation(orgId)])
  // A stubbed or absent backend answers without a body; that is not a connection.
  if (sumR.status !== 'fulfilled' || typeof sumR.value?.connected !== 'boolean') return EMPTY_PLAYBOOK_FACTS
  const summary = sumR.value
  const register = casR.status === 'fulfilled' ? casR.value : null
  const report = remR.status === 'fulfilled' ? remR.value : null

  return {
    connected: true,
    service_connected: summary.connected,
    plans: [],
    issuing_cas: (register?.issuing_cas ?? []).map((ca) => ({
      id: ca.id,
      ca_owner: ca.ca_owner,
      issuing_ca: ca.issuing_ca,
      matched_by: ca.matched_by,
      system_ids: (ca.systems ?? []).map((s) => s.system_id).filter((id): id is string => !!id),
      inventory_names: (ca.systems ?? []).map((s) => s.name),
    })),
    remediations: (report?.items ?? []).map((r) => ({
      key: r.key,
      asset: r.asset,
      asset_type: r.asset_type,
      nist_rule: r.nist_rule,
      nist_remediation: r.nist_remediation,
      asset_count: r.asset_count,
      vulnerable_count: r.vulnerable_count,
      system_ids: r.system_ids ?? [],
    })),
    standard: report?.standard ?? summary.standard ?? null,
  }
}

// The service facts with the local plan set: one PlaybookFacts for the signals.
export function mergePlaybookFacts(service: PlaybookFacts, local: PlaybookFacts): PlaybookFacts {
  return { ...service, connected: true, plans: local.plans }
}

// The actions this module creates from recommendations, as the Playbook plan
// set: an action created from a recommendation is a plan, its state feeds the
// `playbook.plan_*` signals, and the two-way link is the action's objective_code.
export interface LocalPlaybookAction {
  id: string
  title: string
  objective_code: string
  transition: string
  bullet_index: number
  inventory_id: string | null
  owner: string | null
  due_at: string | null
  status: 'open' | 'in_progress' | 'done'
  created_at: string
  updated_at: string
}

export function playbookFactsFromActions(actions: LocalPlaybookAction[]): PlaybookFacts {
  return {
    ...EMPTY_PLAYBOOK_FACTS,
    plans: actions.map((a) => ({
      id: a.id,
      title: a.title,
      inventory_id: a.inventory_id,
      status: a.status === 'open' ? 'draft' : a.status === 'in_progress' ? 'evaluated' : 'done',
      owner: a.owner,
      due_at: a.due_at,
      updated_at: a.updated_at,
    })),
  }
}
